import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components/macro";
import { LoadingIcon } from "../Icons/Icons";

/** Must match the `height: 44px` set on `th` in Table.tsx */
export const TABLE_HEADER_HEIGHT = 44;

type TableStickyLoaderProps = {
  /**
   * Sticky top offset in px. Defaults to TABLE_HEADER_HEIGHT (44px) so the
   * loader appears directly below the sticky header row.
   */
  top?: number;
  /**
   * z-index for the loader overlay.
   * - Use 2 when the loader is rendered *inside* TableWrapper (built-in use).
   * - Use 3 (default) when rendered *outside* TableWrapper as a sibling of
   *   the Table component, so it appears above the sticky header (z-index: 2).
   */
  zIndex?: number;
};

const animateLoadingBar = keyframes`
  0%   { background-position: 100% 0%; }
  100% { background-position:   0% 0%; }
`;

const Wrapper = styled.div`
  position: sticky;
  position: -webkit-sticky;
  left: 0;
  right: 0;
  height: 0;
  overflow: visible;

  .loadingBar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    width: 100%;
    animation: ${animateLoadingBar} 1.5s linear infinite;
    background-image: linear-gradient(
      90deg,
      ${({ theme }) => theme.secondaryBG},
      ${({ theme }) => theme.brandColor},
      ${({ theme }) => theme.secondaryBG},
      ${({ theme }) => theme.brandColor}
    );
    background-size: 300% 100%;
  }
`;

const Badge = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.resolve("color_bg_surface_brand_mixed")};
  padding: 6px 11px 7px;
  border-radius: 20px;
  color: ${({ theme }) => theme.primaryTextColor};
  font-size: 14px;
  line-height: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 104px;
  white-space: nowrap;
  border: 1px solid ${({ theme }) => theme.brandColor};
`;

export function TableStickyLoader({
  top = TABLE_HEADER_HEIGHT,
  zIndex = 3,
}: TableStickyLoaderProps) {
  const { t } = useTranslation();

  return (
    <Wrapper style={{ top, zIndex }}>
      <div className="loadingBar" />
      <Badge>
        <LoadingIcon width={15} height={15} />
        {t("Loading")}...
      </Badge>
    </Wrapper>
  );
}
